import classNames from "classnames";
import * as React from "react";

import { useAuth } from "@/providers";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord, ViewVisibility } from "@/shared/domain/views";
import { RotateCcw, Save } from "@/shared/foundation/icons";
import {
  Popover,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";

import type { ResourceView } from "../useResourceView";

import styles from "./SaveViewControl.module.css";
import { overwriteViewErrorMessage } from "./saveViewError";
import { useLatestLoadedView } from "./useLatestLoadedView";
import type { LoadedView } from "./useLoadedView";
import { useSaveView, type SaveView } from "./useSaveView";

export interface SaveViewControlProps<TData> {
  /** The live resource view whose state is being saved. */
  view: ResourceView<TData>;
  /** Resource type the new View is written against. */
  target: QueryTarget;
  /**
   * The loaded-View session from `useLoadedView`.
   *
   * Passed whole rather than as individual callbacks: saving, discarding and
   * reading what is loaded all have to go through one owner, or they cannot
   * order themselves against each other. Discard applies a View and must share
   * the picker's applier; a save must be judged on completion by the same hook
   * that knows what changed while it was out.
   */
  session: LoadedView;
}

/**
 * Flag gate and wiring for {@link SaveViewMenu}.
 *
 * A wrapper rather than an early return, for the same reason as `ViewPicker`:
 * `useSaveView` calls mutation hooks that reach for `ViewService` whether or
 * not anything renders, so not mounting this is what prevents it.
 */
export function SaveViewControl<TData>({
  view,
  target,
  session,
}: SaveViewControlProps<TData>) {
  const viewsEnabled = useCheckFeatureFlag("release-views");

  if (!viewsEnabled) {
    return null;
  }

  return <SaveViewControlInner view={view} target={target} session={session} />;
}

function SaveViewControlInner<TData>({
  view,
  target,
  session,
}: SaveViewControlProps<TData>) {
  const save = useSaveView({ view, target, session });
  const loadedView = useLatestLoadedView(target, session.loadedView);

  return (
    <SaveViewMenu
      loadedView={loadedView}
      save={save}
      onReset={session.discardChanges}
    />
  );
}

export interface SaveViewMenuProps {
  /** The View currently loaded, or `null` when the filters are ad-hoc. */
  loadedView: ViewRecord | null;
  /**
   * Saving surface from `useSaveView`. Both saves mark their own result loaded;
   * this component's job ends at dismissing the popover.
   */
  save: SaveView;
  /** Throw away the edits and put the loaded View's saved state back. */
  onReset: () => void;
}

/**
 * Save the table's current state as a View, or back to the one that is
 * loaded; or reset it to what is saved.
 *
 * Sits in the toolbar beside the Views chip, and only while the table has
 * moved from what is saved — a filter added, a column dragged, a sort chosen.
 * Column-only changes count: a View can be saved for its layout alone. The
 * pair appears and disappears in the toolbar's own row, so nothing below the
 * toolbar shifts.
 *
 * With a View loaded, "Save" is ambiguous, so the button opens a menu naming
 * both intents: overwrite, or fork. With nothing loaded it opens the form
 * directly rather than offering a menu of one.
 *
 * Reset is the opposite of the Clear beside the Views chip and the filter
 * bar's Clear, which unload the View and empty the table: this keeps the View and

 * puts its saved state back. With nothing loaded it puts the built-in layout
 * back.
 */
export function SaveViewMenu({ loadedView, save, onReset }: SaveViewMenuProps) {
  const { currentOrganization } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<"menu" | "form">("menu");
  const [name, setName] = React.useState("");
  const [visibility, setVisibility] = React.useState<ViewVisibility>("private");
  const [error, setError] = React.useState<string | null>(null);

  const canOverwrite = loadedView !== null;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        // A half-typed name from a dismissed attempt reappearing reads as a bug.
        setMode(canOverwrite ? "menu" : "form");
        setName("");
        setVisibility("private");
        setError(null);
      }
    },
    [canOverwrite],
  );

  const handleCreate = React.useCallback(async () => {
    setError(null);
    try {
      await save.createView({ name: name.trim(), visibility });
      setOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save.");
    }
  }, [name, save, visibility]);

  const handleOverwrite = React.useCallback(async () => {
    setError(null);
    try {
      await save.updateLoadedView();
      setOpen(false);
    } catch (caught) {
      setError(overwriteViewErrorMessage(caught));
    }
  }, [save]);

  // Nothing to save: the table is showing exactly what is saved, whether that
  // is the loaded View or the bare table's own layout.
  if (!save.isDirty) {
    return null;
  }

  return (
    <div className={styles.controls}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <Popover.Trigger
          render={
            <RobotoButton
              variant="secondary"
              icon={<Save size={15} aria-hidden />}
              disabled={save.isSaving}
            >
              Save
            </RobotoButton>
          }
        />
        <Popover.Content align="end" aria-label="Save as view">
          {mode === "menu" ? (
            <div className={styles.menu}>
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => void handleOverwrite()}
              >
                Save to this view
              </button>
              <button
                type="button"
                className={styles.menuItem}
                onClick={() => setMode("form")}
              >
                Create new view&hellip;
              </button>
              {error !== null && (
                <p className={classNames(styles.error, styles.menuError)}>
                  {error}
                </p>
              )}
            </div>
          ) : (
            // A real form, so Enter in the name field submits. The browser only
            // submits implicitly while the submit button is enabled, which keeps
            // the empty-name rule in one place.
            <form
              className={styles.form}
              onSubmit={(event) => {
                event.preventDefault();
                void handleCreate();
              }}
            >
              {/*
              Names the noun: a customer reaches this by changing the table,
              not by looking for Views, and the header is what connects the
              two.
          */}
              <h2 className={styles.formHeader}>Save as new view</h2>

              <label className={styles.label} htmlFor="save-view-name">
                Name
              </label>
              <TextInput
                id="save-view-name"
                className={styles.nameInput}
                value={name}
                placeholder="e.g. Large uploads this week"
                autoFocus
                // Chrome's history suggestions would cover the placeholder.
                autoComplete="off"
                onChange={(event) => setName(event.target.value)}
              />

              <fieldset className={styles.visibilityGroup}>
                <legend className={styles.label}>Who can access it</legend>
                <label className={styles.visibilityOption}>
                  <input
                    type="radio"
                    name="save-view-visibility"
                    checked={visibility === "private"}
                    onChange={() => setVisibility("private")}
                  />
                  Only me
                </label>
                <label className={styles.visibilityOption}>
                  <input
                    type="radio"
                    name="save-view-visibility"
                    checked={visibility === "organization"}
                    onChange={() => setVisibility("organization")}
                  />
                  {/*
                  Named, not "my org": a customer in several orgs cannot tell
                  which one they are about to share with otherwise. The
                  fallback is only reachable before auth resolves, which this
                  control is not normally rendered without.
              */}
                  Everyone in {currentOrganization?.name ?? "my organization"}
                </label>
              </fieldset>

              {error !== null && <p className={styles.error}>{error}</p>}

              <div className={styles.actions}>
                <RobotoButton
                  type="button"
                  variant="secondary"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </RobotoButton>
                <RobotoButton
                  type="submit"
                  variant="primary"
                  // Non-empty is the only rule: names are not unique (ENG-2876).
                  disabled={name.trim().length === 0 || save.isSaving}
                >
                  Save
                </RobotoButton>
              </div>
            </form>
          )}
        </Popover.Content>
      </Popover>
      <RobotoButton
        variant="secondary"
        icon={<RotateCcw size={15} aria-hidden />}
        title={
          loadedView === null
            ? "Reset to the default layout"
            : `Reset to ${loadedView.name}`
        }
        onClick={onReset}
      >
        Reset
      </RobotoButton>
    </div>
  );
}
